import { Component, ElementRef, OnDestroy, OnInit, ViewChild, WritableSignal, inject, signal } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormGroupDirective } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { Matter } from '../shared/models/matter';
import { Participants, formatClientName } from '../shared/models/participants';
import { CommonService } from '../shared/services/common.service';
import { MatterService } from '../shared/services/matter.service';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { AddJournalRequestDto } from '../shared/models/journalRequestDto';
import { RequestType } from '../enums/request-type.enum';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserService } from '../shared/services/user.service';
import { Authoriser } from '../shared/models/authoriser';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { validateJournalTransfer } from '../shared/utils/journal-validation';
import { DraftEditor, DraftSaveOutcome } from '../shared/draft-editor/draft-editor';
import { DraftSnapshot, readBoolean, readNumber, readString } from '../shared/draft-editor/draft-snapshot';

@Component({
    selector: 'xp-journal-request',
    templateUrl: './journal-request.component.html',
    styleUrl: './journal-request.component.scss',
    providers: [DraftEditor]
})
export class JournalRequestComponent implements OnInit, OnDestroy {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    /** Saves this form as a draft and reopens saved drafts. */
    readonly draftEditor = inject(DraftEditor);
    private readonly route = inject(ActivatedRoute);

    // ── Public state ─────────────────────────────────────────────────────────
    displayFile: string[] = [];
    fileAttachments: AttachmentData[] = [];
    journalRequestForm!: FormGroup;
    currentDate: Date = new Date();
    isLoading = false;
    noData = false;
    matterNo = '';
    matterDesc = '';
    matterFromDropdownData: Matter[] = [];
    matterToDropdownData: Matter[] = [];
    displayValue: string | null = '';
    submitBtn = false;
    matterFromNo = '';
    matterToNo = '';
    isUserPartner: boolean | null = null;
    actionstepMatterUrl1 = '';
    showLinkToOpenMatter1: WritableSignal<boolean> = signal(false);
    actionstepMatterUrl2 = '';
    showLinkToOpenMatter2: WritableSignal<boolean> = signal(false);
    partnerList: Authoriser[] = [];
    filteredBankAccounts: BankAccount[] = [];
    actionstepMatterOverviewUrl1 = '';
    actionstepMatterOverviewUrl2 = '';
    matterToCachedData: Matter[] = [];
    isMatterToLoading = false;

    private readonly destroy$ = new Subject<void>();
    private actionstepBaseUrl = '';
    private actionstepOverviewBaseUrl = '';
    private bankAccounts: BankAccount[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private commonService: CommonService,
        private matterService: MatterService,
        private snackbarService: SnackbarService,
        private requestService: RequestsService,
        private manageUserRoleService: ManageUserRoleService,
        private userService: UserService,
        private bankAccountService: BankAccountService
    ) { }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    async ngOnInit(): Promise<void> {
        this.draftEditor.configure(RequestType.Journal);
        this.buildForm();
        this.draftEditor.watchForm(() => ({ snapshot: this.buildDraftSnapshot(), pendingFiles: this.fileAttachments.length }));
        await this.loadInitialData();
        // Before the matter search is wired up: changing "Matter From" there clears amount, reason, files and "Matter To".
        await this.restoreDraftFromRoute();
        this.setupMatterAutocomplete();
        this.draftEditor.markClean();
    }

    /** Whether the form holds changes that have not been saved. Used to warn the user before they leave. */
    hasUnsavedChanges(): boolean {
        return this.draftEditor.hasUnsavedChanges();
    }

    // ── Drafts ────────────────────────────────────────────────────────────────

    /** True when there is something worth saving as a draft: the user has typed something, or this is a saved draft. */
    get canSaveDraft(): boolean {
        return this.journalRequestForm.dirty || this.fileAttachments.length > 0 || this.draftEditor.draftId() !== null;
    }

    /**
     * Saves the form as a draft, however incomplete it is.
     * @returns True only when the draft and all its new documents were saved.
     */
    async saveDraft(): Promise<boolean> {
        const f = this.journalRequestForm.getRawValue();
        const amount = parseFloat(String(f.amount ?? '').replace(/,/g, ''));
        const descriptions = [f.matterFrom, f.matterTo].map(matter => this.describeMatter(matter)).filter(text => text);
        const clients = [f.clientName1, f.clientName2].filter((name: string) => !!name);

        const outcome = await this.draftEditor.save({
            snapshot: this.buildDraftSnapshot(),
            summary: {
                // Documents are filed under the "from" matter, so that is the matter number a Journal draft reports.
                matterNo: Number(this.matterFromNo) || null,
                matter: descriptions.length ? descriptions.join(' → ') : null,
                clientName: clients.length ? clients.join(' → ') : null,
                amount: Number.isFinite(amount) ? amount : null,
                reason: f.reason || null
            },
            newFiles: [...this.fileAttachments]
        });

        if (outcome === DraftSaveOutcome.Saved) {
            this.clearUpload();
        }
        return outcome === DraftSaveOutcome.Saved;
    }

    private describeMatter(matter: unknown): string {
        return typeof matter === 'string' ? matter.split(' - ').slice(1).join(' - ') : '';
    }

    /**
     * Handles the Save as Draft button: saves the draft and, once everything is saved, clears the form for the next request,
     * as after a submit. If anything could not be saved, the form stays as it is.
     * @param formDirective The form's directive, so the form is reset together with its submitted state.
     */
    async saveDraftAndReset(formDirective: FormGroupDirective): Promise<void> {
        if (!await this.saveDraft()) return;

        this.resetForm(formDirective);
        await this.draftEditor.afterDraftSaved();
    }

    private async restoreDraftFromRoute(): Promise<void> {
        const draftId = Number(this.route.snapshot.paramMap.get('draftId'));
        if (!Number.isInteger(draftId) || draftId <= 0) return;

        const snapshot = await this.draftEditor.open(draftId);
        if (snapshot) {
            await this.applyDraftSnapshot(snapshot);
        }
    }

    private buildDraftSnapshot(): DraftSnapshot {
        const f = this.journalRequestForm.getRawValue();
        const accountId = (account: BankAccount | '' | null): string => (account ? account.accountId : '');

        return {
            values: {
                matterFrom: typeof f.matterFrom === 'string' ? f.matterFrom : '',
                matterTo: typeof f.matterTo === 'string' ? f.matterTo : '',
                clientName1: f.clientName1 ?? '',
                clientName2: f.clientName2 ?? '',
                clientBankAccount1Id: accountId(f.clientBankAccount1),
                clientBankAccount2Id: accountId(f.clientBankAccount2),
                partnerAuthorisingId: f.partnerAuthorisingId ?? null,
                reason: f.reason ?? '',
                amount: f.amount ?? '',
                fundsCheckedByPartner: f.fundsCheckedByPartner === true
            },
            state: { matterFromNo: this.matterFromNo, matterToNo: this.matterToNo }
        };
    }

    private async applyDraftSnapshot(snapshot: DraftSnapshot): Promise<void> {
        const { values, state } = snapshot;
        const form = this.journalRequestForm;

        this.matterFromNo = readString(state, 'matterFromNo');
        this.matterToNo = readString(state, 'matterToNo');

        if (this.matterFromNo) {
            this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}`;
            this.actionstepMatterOverviewUrl1 = `${this.actionstepOverviewBaseUrl}/${this.matterFromNo}`;
            this.showLinkToOpenMatter1.set(true);
            form.get('matterTo')?.enable();
        }
        if (this.matterToNo) {
            this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}`;
            this.actionstepMatterOverviewUrl2 = `${this.actionstepOverviewBaseUrl}/${this.matterToNo}`;
            this.showLinkToOpenMatter2.set(true);
        }

        const account1 = readString(values, 'clientBankAccount1Id');
        const account2 = readString(values, 'clientBankAccount2Id');
        const partnerId = readNumber(values, 'partnerAuthorisingId');
        form.patchValue({
            matterFrom: readString(values, 'matterFrom'),
            matterTo: readString(values, 'matterTo'),
            clientName1: readString(values, 'clientName1'),
            clientName2: readString(values, 'clientName2'),
            clientBankAccount1: this.bankAccounts.find(account => account.accountId === account1) ?? '',
            clientBankAccount2: this.bankAccounts.find(account => account.accountId === account2) ?? '',
            // A saved partner who has since lost that role is no longer in the list; the user must choose again.
            partnerAuthorisingId: this.partnerList.some(partner => partner.id === partnerId) ? partnerId : null,
            reason: readString(values, 'reason'),
            amount: readString(values, 'amount'),
            fundsCheckedByPartner: readBoolean(values, 'fundsCheckedByPartner')
        });

        // Submit stays disabled until the form counts as touched. Touch the form itself, not every field,
        // so the user is not shown an error on everything they have not filled in yet.
        form.markAsTouched();

        if (this.matterFromNo) {
            await this.loadMatterToOptions(Number(this.matterFromNo));
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Form construction ─────────────────────────────────────────────────────

    private buildForm(): void {
        this.journalRequestForm = this.formBuilder.group(
            {
                // Matter
                matterFrom: ['', Validators.required],
                matterTo: [{ value: '', disabled: true }, Validators.required],

                clientName1: [''],
                clientName2: [''],

                // Bank accounts
                clientBankAccount1: ['', Validators.required],
                clientBankAccount2: ['', Validators.required],

                // FK — user-provided
                partnerAuthorisingId: [null],

                // Journal details
                reason: ['', Validators.required],
                amount: ['', [Validators.required, Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)]],

                // Date — matches DTO field dateTransaction
                dateRequested: [new Date(), Validators.required],
                requestedTime: [this.toTimeString(new Date()), Validators.required],

                // File attachments (not sent in DTO body; uploaded separately)
                uploadedFiles: [[]],
                fundsCheckedByPartner: [false],
            },
            { validators: this.matterValidation }
        );
    }

    // ── Data loading ──────────────────────────────────────────────────────────

    private async loadInitialData(): Promise<void> {
        try {
            await Promise.all([this.loadUserRole(), this.loadDropdownData()]);
        } catch {
            this.snackbarService.errorSnackBar('Failed to load initial data. Please refresh the page.');
        }
    }

    private async loadUserRole(): Promise<void> {
        const role = await this.manageUserRoleService.getUserPermissionsByRole(this.userService.userDetails?.userRole!);
        this.isUserPartner = role.partnerApprove;

        this.updatePartnerAuthorisingValidator();
    }

    private async loadDropdownData(): Promise<void> {
        const [bankAccounts, partners, actionstepUrl, actionstepOverviewUrl, matters] = await Promise.all([
            this.bankAccountService.getAllBankAccounts(),
            this.requestService.getPartnersList(),
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
            this.matterService.getMatterList('', ''),
        ]);

        this.bankAccounts = bankAccounts;
        this.filteredBankAccounts = [...bankAccounts];
        this.partnerList = partners;
        this.actionstepBaseUrl = actionstepUrl;
        this.actionstepOverviewBaseUrl = actionstepOverviewUrl;
        this.matterFromDropdownData = matters;
        this.matterToDropdownData = matters;
    }

    async lookupSelectionChanged(event: MatOptionSelectionChange<string>, matter: string): Promise<void> {
        if (!event.isUserInput) return;
        const res = event.source.viewValue;
        this.matterNo = res.split(' - ')[0];
        this.matterDesc = res.split(' - ')[1];
        matter === 'matterFrom' ? this.matterFromNo = this.matterNo : this.matterToNo = this.matterNo;

        if (matter === 'matterFrom') {
            this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}`;
            this.actionstepMatterOverviewUrl1 = `${this.actionstepOverviewBaseUrl}/${this.matterFromNo}`;
            this.showLinkToOpenMatter1.set(true);
            this.journalRequestForm.get('matterTo')?.enable();

            this.loadClientDetails(Number(this.matterNo), 'from');
            await this.loadMatterToOptions(Number(this.matterNo));
        } else {
            this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}`;
            this.actionstepMatterOverviewUrl2 = `${this.actionstepOverviewBaseUrl}/${this.matterToNo}`;
            this.showLinkToOpenMatter2.set(true);
            this.loadClientDetails(Number(this.matterNo), 'to');
        }
    }

    private async loadMatterToOptions(matterFromNumber: number): Promise<void> {
        this.matterToCachedData = [];
        this.matterToDropdownData = [];
        this.isMatterToLoading = true;

        try {
            const participants: Participants[] = await this.matterService.getMatterClient(matterFromNumber, 27);

            const allMatters: Matter[] = [];
            for (const participant of participants ?? []) {
                const matters = await this.matterService.getMatterListByParticipant(participant.id);
                allMatters.push(...matters);
            }

            const availableMatters = allMatters.filter(m => m.number !== matterFromNumber);
            this.matterToCachedData = availableMatters;
            this.matterToDropdownData = availableMatters;
        } catch {
            this.snackbarService.errorSnackBar('Failed to load matters for the selected client.');
        } finally {
            this.isMatterToLoading = false;
        }
    }

    private loadClientDetails(matterNumber: number, type: 'from' | 'to'): void {
        this.matterService.getMatterClient(matterNumber, 27)
            .then((res: Participants[]) => {
                const clientName = res?.length
                    ? res.map(formatClientName).join(', ')
                    : '';

                if (type === 'from') {
                    this.journalRequestForm.patchValue({
                        clientName1: clientName
                    });
                } else {
                    this.journalRequestForm.patchValue({
                        clientName2: clientName
                    });
                }
            })
            .catch(() => {
                if (type === 'from') {
                    this.journalRequestForm.patchValue({ clientName1: '' });
                } else {
                    this.journalRequestForm.patchValue({ clientName2: '' });
                }
            });
    }


    private matterValidation(formGroup: FormGroup): void {
        const matterFromControl = formGroup.get('matterFrom');
        const matterToControl = formGroup.get('matterTo');

        if (!matterFromControl || !matterToControl) return;

        const matterFrom = matterFromControl.value;
        const matterTo = matterToControl.value;

        if (matterToControl.hasError('sameMatter')) {
            const currentErrors = { ...matterToControl.errors };
            delete currentErrors['sameMatter'];
            if (Object.keys(currentErrors).length === 0) {
                matterToControl.setErrors(null);
            } else {
                matterToControl.setErrors(currentErrors);
            }
        }
    }

    private setupMatterAutocomplete(): void {
        this.journalRequestForm.controls['matterFrom'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query) => {
                this.resetDependentFields();
                const isNumeric = !isNaN(Number(query));
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(
                    isNumeric ? (query ?? '') : '',
                    isNumeric ? '' : (query ?? '')
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (data) => {
                this.noData = false;
                this.matterFromDropdownData = data;
                this.isLoading = false;
            },
            error: (error) => {
                this.noData = true;
                this.isLoading = false;
                if (error.status === 401) {
                    this.commonService.apiSecurityError();
                }
            }
        });

        this.journalRequestForm.get('amount')?.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe((value) => {
            const numberValue = value ? value.toString().replace(/,/g, '') : '';
            this.journalRequestForm.get('amount')?.setValue(numberValue, { emitEvent: false });
        });
    }

    private resetDependentFields(): void {
        this.journalRequestForm.patchValue({
            amount: '',
            reason: '',
            uploadedFiles: []
        });

        this.journalRequestForm.controls['amount'].markAsPristine();
        this.journalRequestForm.controls['reason'].markAsPristine();
        this.journalRequestForm.controls['amount'].markAsUntouched();
        this.journalRequestForm.controls['reason'].markAsUntouched();

        this.displayFile = [];
        this.fileAttachments = [];
        this.matterDesc = '';
        this.resetMatterTo();
    }

    private resetMatterTo(): void {
        this.matterToNo = '';
        this.showLinkToOpenMatter2.set(false);
        this.actionstepMatterUrl2 = '';
        this.actionstepMatterOverviewUrl2 = '';
        this.journalRequestForm.controls['matterTo'].reset();
        this.journalRequestForm.patchValue({ clientName2: '' });
    }

    formatNumber(): void {
        if (this.journalRequestForm.get('amount')?.value == 0) {
            this.displayValue = '';
            this.journalRequestForm.get('amount')?.setValue(this.displayValue);
        } else {
            this.displayValue = this.commonService.formatNumberWithoutRounding(this.journalRequestForm.get('amount')?.value);
            this.journalRequestForm.get('amount')?.setValue(this.displayValue);
        }
    }


    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private updatePartnerAuthorisingValidator(): void {
        const ctrl = this.journalRequestForm.get('partnerAuthorisingId');
        const fundsCtrl = this.journalRequestForm.get('fundsCheckedByPartner');

        if (this.isUserPartner) {
            ctrl?.clearValidators();
            fundsCtrl?.setValidators(Validators.requiredTrue);
        } else {
            ctrl?.setValidators([Validators.required]);
            fundsCtrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/;
        const key = event.key;
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) return;
        if (!allowedKeys.test(key)) event.preventDefault();
    }

    async clearSearchFilter(matter: string): Promise<void> {
        if (matter === 'from') {
            this.showLinkToOpenMatter1.set(false);
            this.actionstepMatterUrl1 = '';
            this.actionstepMatterOverviewUrl1 = '';
            this.journalRequestForm.controls['matterFrom'].reset();
            this.matterFromDropdownData = [];
            this.showLinkToOpenMatter2.set(false);
            this.actionstepMatterUrl2 = '';
            this.actionstepMatterOverviewUrl2 = '';
            this.journalRequestForm.controls['matterTo'].reset();
            this.matterToDropdownData = [];
            this.journalRequestForm.get('matterTo')?.disable();
        } else {
            this.matterToDropdownData = this.matterToCachedData;
            this.resetMatterTo();
        }
    }

    displayMatter(matter: Matter): string {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    filterBankAccounts(): void {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    onFilesSelected(input: any): void {
        if (input.files) {
            this.fileAttachments = [];
            this.displayFile = [];
            const filesArray = Array.from(input.files);

            filesArray.forEach((file: any) => {
                const fileName = file.name;
                this.displayFile.push(fileName);
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const bytes = e.target.result.split('base64,')[1];
                    this.fileAttachments.push({ fileName: fileName, attachments: bytes });
                };
                reader.readAsDataURL(file);
            });
        }
        this.journalRequestForm.get('uploadedFiles')?.setValue(this.fileAttachments);
    }

    clearUpload(): void {
        this.displayFile = [];
        this.fileAttachments = [];
        this.journalRequestForm.get('uploadedFiles')?.setValue([]);
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
        this.journalRequestForm.get('uploadedFiles')?.reset();
    }

    // ── Submit ─────────────────────────────────────────────────────────────────

    async submit(formDirective: FormGroupDirective): Promise<void> {
        // The Enter key can submit even when the button is disabled; a draft that is still being saved must not be submitted.
        if (this.draftEditor.saving()) return;

        if (this.journalRequestForm.invalid) {
            this.journalRequestForm.markAllAsTouched();
            this.snackbarService.errorSnackBar('Please complete all required fields.');
            return;
        }

        if (!Number(this.matterToNo)) {
            this.journalRequestForm.controls['matterTo'].setErrors({ required: true });
            this.journalRequestForm.markAllAsTouched();
            this.snackbarService.errorSnackBar('Please select a Matter To from the list.');
            return;
        }

        this.submitBtn = true;
        let requestCreated = false;

        try {
            const f = this.journalRequestForm.value;

            const datePart: Date = f.dateRequested instanceof Date ? f.dateRequested : new Date(f.dateRequested);

            const [hours, minutes] = (f.requestedTime ?? '00:00').split(':').map(Number);

            const requestedDate = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, 0, 0);

            const postData: AddJournalRequestDto = {
                draftId: this.draftEditor.draftId() ?? undefined,
                matterFromNo: Number(this.matterFromNo),
                matterToNo: Number(this.matterToNo),
                matterFrom: f.matterFrom,
                matterTo: f.matterTo,
                clientName1: f.clientName1,
                clientName2: f.clientName2,
                clientBankAccountName1: f.clientBankAccount1
                    ? `${(f.clientBankAccount1 as BankAccount).accountId} - ${(f.clientBankAccount1 as BankAccount).description}`
                    : undefined,
                clientBankAccountName2: f.clientBankAccount2
                    ? `${(f.clientBankAccount2 as BankAccount).accountId} - ${(f.clientBankAccount2 as BankAccount).description}`
                    : undefined,
                clientBankAccountId1: f.clientBankAccount1 ? (f.clientBankAccount1 as BankAccount).accountId : undefined,
                clientBankAccountId2: f.clientBankAccount2 ? (f.clientBankAccount2 as BankAccount).accountId : undefined,
                partnerAuthorisingId: f.partnerAuthorisingId,
                amount: parseFloat(f.amount) || 0,
                reason: f.reason,
                dateRequested: requestedDate,
                fundsCheckedByPartner: f.fundsCheckedByPartner ?? false,
            };

            const transferError = validateJournalTransfer(
                postData.matterFromNo,
                postData.matterToNo,
                postData.clientBankAccountId1,
                postData.clientBankAccountId2
            );

            if (transferError) {
                this.snackbarService.errorSnackBar(transferError);
                this.submitBtn = false;
            } else {
                const res = await this.requestService.addJournalRequest(postData);

                if (res) {
                    requestCreated = true;
                    this.snackbarService.successSnackBar('Journal request sent successfully!');

                    if (this.journalRequestForm.get('uploadedFiles')?.value?.length > 0) {
                        const docData: RequestDocAttachmentResponse = {
                            requestId: res.id!,
                            requestType: RequestType.Journal,
                            attachments: this.journalRequestForm.get('uploadedFiles')?.value
                        };
                        try {
                            await this.requestService.addRequestDocument(docData);
                            this.snackbarService.successSnackBar('Documents uploaded successfully!');
                        } catch {
                            this.snackbarService.errorSnackBar('Failed to add new request document');
                        }
                    }

                    this.resetForm(formDirective);
                }
            }

        } catch {
            this.snackbarService.errorSnackBar('Failed to send journal request. Please try again.');
        } finally {
            this.submitBtn = false;
            if (requestCreated) {
                // The draft (if any) was consumed when the request was created, whatever happened to the documents.
                await this.draftEditor.afterSubmit();
            }
        }
    }

    // ── Reset ──────────────────────────────────────────────────────────────────

    resetForm(formDirective?: FormGroupDirective): void {
        this.showLinkToOpenMatter1.set(false);
        this.actionstepMatterUrl1 = '';
        this.actionstepMatterOverviewUrl1 = '';
        this.showLinkToOpenMatter2.set(false);
        this.actionstepMatterUrl2 = '';
        this.actionstepMatterOverviewUrl2 = '';
        this.displayFile = [];
        this.fileAttachments = [];
        this.matterFromNo = '';
        this.matterToNo = '';
        this.matterNo = '';
        this.matterDesc = '';

        const defaults = {
            dateRequested: new Date(),
            requestedTime: this.toTimeString(new Date()),
        };

        if (formDirective) {
            formDirective.resetForm(defaults);
        } else {
            this.journalRequestForm.reset(defaults);
        }

        this.journalRequestForm.get('matterTo')?.disable();
        this.updatePartnerAuthorisingValidator();

        setTimeout(() => {
            this.journalRequestForm.markAsPristine();
            this.journalRequestForm.markAsUntouched();
        }, 0);
    }

    onUrlCopied(): void {
        this.snackbarService.successSnackBar('URL copied to clipboard');
    }
}


